import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { LanguageContext } from "../../Languages/LanguageContext";
import { fetchRevenuechart } from "../../../features/dashboard/newBuilderSlice";

const PerformanceWaveChart = () => {
  const { t } = useContext(LanguageContext);
  const dispatch = useDispatch();
  const revenueChart  = useSelector((state) => state.dashboard?.revenueChart?.data);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const allMonths = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState("");

  // Fetch data on year/month change
  useEffect(() => {
    if (selectedYear) {
      const params = { year: selectedYear };
      if (selectedMonth) params.month = selectedMonth;
      dispatch(fetchRevenuechart(params));
    }
  }, [selectedYear, selectedMonth, dispatch]);

  // Process API response into full 12-month array
  const chartData = (() => {
    if (!revenueChart?.data) return [];

    // Wrap single object into array if needed
    const dataArray = Array.isArray(revenueChart.data)
      ? revenueChart.data
      : [revenueChart.data];

    // Map API data by month name
    const mappedData = dataArray.map((item) => ({
      month: item.month,
      name: item.month_name,
      revenue: item.revenue,
    }));

    // Ensure all months are present
    const fullData = allMonths.map((month, i) => {
      const monthObj = mappedData.find((d) => d.name === month);
      return monthObj || { name: month, revenue: 0 };
    });

    // If a month is selected, zero out others
    if (selectedMonth) {
      const monthIndex = parseInt(selectedMonth, 10) - 1;
      return fullData.map((d, i) => ({
        ...d,
        revenue: i === monthIndex ? d.revenue : 0,
      }));
    }

    return fullData;
  })();

  return (
    <div
      className="card shadow-sm p-2 rounded w-100 mx-auto"
      style={{ maxWidth: "800px", height: "400px" }}
    >
      <div className="card-body">
        {/* Header with filters */}
        <div className="d-sm-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-3 text-light">
            {t.revenue || "Revenue"}
          </h5>

          <div className="d-flex justify-content-end align-items-center mb-3">
            <select
              className="form-select form-select-sm me-2 "
              style={{ width: "100px" }}
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            <select
              className="form-select form-select-sm"
              style={{ width: "120px" }}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="">All Months</option>
              {allMonths.map((month, i) => (
                <option key={i} value={String(i + 1).padStart(2, "0")}>
                  {month}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Chart Section */}
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3f5263" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#3f5263" stopOpacity={0} />
              </linearGradient>
            </defs>

            <XAxis dataKey="name" />
            <YAxis />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip
              formatter={(value) => [`$${value.toFixed(2)}`, "Revenue"]}
            />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="#39866d"
              fill="url(#colorRevenue)"
              strokeWidth={3}
              animationDuration={1500}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PerformanceWaveChart;
